import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { 
  ArrowLeft, Share2, MapPin, MessageCircle, 
  ShieldCheck, AlertTriangle, X, Send, Loader2,
  ExternalLink, Info, Copy, Check, Twitter
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Groq from "groq-sdk";
import { Helmet } from 'react-helmet-async';
import rehypeRaw from 'rehype-raw';

const rawApiKey = process.env.REACT_APP_GROQ_API_KEY;
const groq = new Groq({ 
  apiKey: rawApiKey || 'dummy_key', 
  dangerouslyAllowBrowser: true 
});

const DetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- STATE UNTUK CHATBOT ---
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [userMsg, setUserMsg] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  // --- STATE UNTUK SHARE ---
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchIdea();
  }, [slug]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, isTyping]);

  const fetchIdea = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ideas')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error || !data) {
      navigate('/');
    } else {
      setIdea(data);
    }
    setLoading(false);
  };

  const openChat = () => {
    setIsChatOpen(true);
    if (chatHistory.length === 0) {
      setChatHistory([
        { 
          role: 'ai', 
          content: `Halo! 👋 Saya **Mentor AI Mikropedia**. \n\nAda yang bisa saya bantu terkait ide bisnis **${idea.judul}** ini? Kamu bisa tanya soal rincian modal, cara cari supplier, atau strategi pemasarannya.` 
        }
      ]);
    }
  };

  const askAI = async (e) => {
    e.preventDefault();
    if (!userMsg.trim() || isTyping) return;

    const currentMsg = userMsg;
    setChatHistory(prev => [...prev, { role: 'user', content: currentMsg }]);
    setUserMsg("");
    setIsTyping(true);

    try {
      const chatCompletion = await groq.chat.completions.create({
        messages: [
          {
            role: "system",
            content: `Kamu adalah Mentor Bisnis Pintar untuk platform Mikropedia. 
            Tugasmu membantu warga memahami ide bisnis: ${idea.judul}. 
            Data konteks: Modal ${idea.modal_min}, Risiko ${idea.risk_level}, Kesulitan ${idea.kesulitan}/5.
            Berikan jawaban yang sangat praktis, singkat, dan gunakan bahasa Indonesia yang ramah.
            WAJIB menggunakan format Markdown untuk setiap jawaban:
            1. Gunakan Bold (**) untuk istilah penting.
            2. Gunakan Bullet points ( - ) atau penomoran untuk daftar.
            3. Berikan JARAK 2 baris antar poin agar tidak menumpuk.
            4. Gunakan header (###) untuk judul bagian.`
          },
          ...chatHistory.map(chat => ({
            role: chat.role === 'ai' ? 'assistant' : 'user',
            content: chat.content
          })),
          { role: "user", content: currentMsg }
        ],
        model: "llama-3.3-70b-versatile",
        temperature: 0.7,
      });

      const botReply = chatCompletion.choices[0]?.message?.content || "Maaf, mentor sedang offline.";
      setChatHistory(prev => [...prev, { role: 'ai', content: botReply }]);
    } catch (err) {
      setChatHistory(prev => [...prev, { role: 'ai', content: "Gagal terhubung ke mentor." }]);
    } finally {
      setIsTyping(false);
    }
  };

  const formatRp = (v) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(v);

  // --- LOGIKA SHARE ---
  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToWA = () => {
    const text = `Cek ide bisnis keren ini: ${idea.judul} di Mikropedia! %0A%0A${window.location.href}`;
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareToTwitter = () => {
    const text = `Ide bisnis: ${idea.judul} - ${idea.summary}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${window.location.href}`, '_blank');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-emerald-600" size={40} />
    </div>
  );

  return (
    <div className="bg-[#f8fafc] min-h-screen font-['Onest'] pb-10 relative">
      {/* --- SEO DYNAMICS WITH HELMET --- */}
      <Helmet>
        <title>{idea?.judul} - Mikropedia</title>
        <meta name="description" content={idea?.summary} />
        {/* Ini adalah Tag Kanonis Dinamis Anda */}
        <link rel="canonical" href={`https://mikropedia.com/ide/${slug}`} />
      </Helmet>
      <nav className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-100">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors font-bold text-sm">
            <ArrowLeft size={20} /> Kembali
          </Link>
          <button onClick={() => setIsShareOpen(true)} className="p-3 bg-slate-50 rounded-2xl text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 transition-all active:scale-90">
            <Share2 size={20} />
          </button>
        </div>
      </nav>

      <main className="container mx-auto px-4 mt-8">
        <div className="grid lg:grid-cols-12 gap-12">
          
          {/* KOLOM KIRI */}
          <div className="lg:col-span-7">
            <div className="relative group mb-8">
              <div className="relative overflow-hidden rounded-[3rem] shadow-2xl">
                <img src={idea.img_url} alt={idea.judul} className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-700" />
                
                {idea.img_attribution_name && (
                  <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-[10px] text-white px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    Photo by <a href={idea.img_attribution_link} target="_blank" rel="noreferrer" className="underline">{idea.img_attribution_name}</a> on <a href="https://unsplash.com/?utm_source=mikropedia&utm_medium=referral" target="_blank" rel="noreferrer">Unsplash</a>
                  </div>
                )}
              </div>

              <div className="py-6 flex gap-3">
                <span className="bg-white px-5 py-2 rounded-2xl font-bold text-xs shadow-sm border border-slate-100">{idea.tag}</span>
                <span className="bg-emerald-600 text-white px-5 py-2 rounded-2xl font-bold text-xs shadow-lg">{idea.legalitas}</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">{idea.judul}</h1>
            <p className="text-lg text-slate-500 leading-relaxed mb-10 prose prose-slate max-w-none"><ReactMarkdown rehypePlugins={[rehypeRaw]}>{idea.summary}</ReactMarkdown></p>

            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-100 shadow-sm mb-8">
              <h3 className="text-xl font-bold mb-8 flex items-center gap-3 text-slate-800">
                <span className="w-2 h-8 bg-emerald-600 rounded-full"></span>
                Langkah Strategis Memulai
              </h3>
              <div className="space-y-6">
                {idea.langkah_mulai?.split(';').map((step, i) => (
                  <div key={i} className="flex gap-6 group">
                    <div className="w-10 h-10 rounded-2xl bg-slate-50 flex-shrink-0 flex items-center justify-center font-bold text-slate-400 group-hover:bg-emerald-600 group-hover:text-white transition-all italic">
                      {i + 1}
                    </div>
                    <p className="text-slate-600 font-medium pt-2 leading-relaxed">{step.trim()}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* KOLOM KANAN */}
          <div className="lg:col-span-5">
            <div className="sticky top-32 space-y-8">
              <div className="bg-slate-900 rounded-[2.5rem] p-10 text-white shadow-2xl">
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-[0.3em] mb-4">Estimasi Modal</p>
                <h2 className="text-4xl font-black mb-2 tracking-tight">{formatRp(idea.modal_min)}</h2>
                <p className="text-slate-500 text-sm mb-8 italic">Maksimal pengeluaran: {formatRp(idea.modal_max)}</p>
                
                <div className="space-y-4 pt-8 border-t border-white/10">
                  <h4 className="font-bold text-xs text-slate-500 uppercase tracking-widest">Rincian:</h4>
                  {idea.rincian_modal?.split(';').map((item, i) => (
                    <div key={i} className="flex justify-between text-sm text-slate-300">
                      <span>{item.split(':')[0]}</span>
                      <span className="font-bold text-white">{item.split(':')[1] || '✓'}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-[2rem] border border-slate-100 flex flex-col items-center text-center">
                  <ShieldCheck className="text-emerald-500 mb-3" size={32} />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Risiko</p>
                  <p className="font-bold text-slate-900">{idea.risk_level}</p>
                </div>
                <div className="bg-white p-6 rounded-[2rem] border border-slate-100 flex flex-col items-center text-center">
                  <AlertTriangle className="text-amber-500 mb-3" size={32} />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Kesulitan</p>
                  <p className="font-bold text-slate-900">{idea.kesulitan}/5</p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <button onClick={openChat} className="flex items-center justify-center gap-3 w-full bg-emerald-600 text-white py-6 rounded-[2rem] font-bold hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-95">
                  <MessageCircle size={20} /> Konsultasi Mentor AI
                </button>
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(idea.judul + ' supplier')}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-3 w-full bg-white text-slate-900 border border-slate-200 py-6 rounded-[2rem] font-bold hover:bg-slate-50 transition-all shadow-sm">
                  <MapPin size={20} /> Cari Supplier Terdekat
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* --- MODAL SHARE UI --- */}
      {isShareOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setIsShareOpen(false)}></div>
          <div className="relative bg-white w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-black text-xl uppercase tracking-tighter text-slate-800">Bagikan Ide</h3>
              <button onClick={() => setIsShareOpen(false)} className="p-2 bg-slate-50 rounded-full text-slate-400 hover:bg-slate-100 transition-all">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              <button onClick={shareToWA} className="w-full flex items-center gap-4 p-4 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#075E54] rounded-2xl transition-all font-bold text-sm">
                <div className="w-10 h-10 bg-[#25D366] rounded-xl flex items-center justify-center text-white"><MessageCircle size={20} fill="currentColor" /></div>
                WhatsApp
              </button>
              <button onClick={shareToTwitter} className="w-full flex items-center gap-4 p-4 bg-sky-50 hover:bg-sky-100 text-sky-600 rounded-2xl transition-all font-bold text-sm">
                <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center text-white"><Twitter size={20} fill="currentColor" /></div>
                Twitter / X
              </button>
              <div className="h-[1px] bg-slate-100 my-4"></div>
              <div className="relative">
                <input readOnly value={window.location.href} className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-4 text-[10px] font-medium text-slate-400 outline-none" />
                <button onClick={handleCopyLink} className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                  {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- CHATBOT UI --- */}
      {isChatOpen && (
        <div className="fixed z-[100] bg-white flex flex-col shadow-2xl transition-all duration-300 inset-0 w-full h-full rounded-none md:inset-auto md:bottom-6 md:right-6 md:w-[420px] md:h-[600px] md:rounded-[2.5rem] md:border md:border-slate-100 overflow-hidden animate-in fade-in slide-in-from-bottom-5">
          <div className="bg-slate-900 p-6 text-white flex justify-between items-center shrink-0">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-500 rounded-2xl flex items-center justify-center">
                <MessageCircle size={20} />
              </div>
              <p className="font-black text-sm uppercase italic tracking-tight">Mentor AI</p>
            </div>
            <button onClick={() => setIsChatOpen(false)} className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-slate-50/50">
            {chatHistory.map((chat, i) => (
              <div key={i} className={`flex ${chat.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-4 text-[14px] leading-relaxed shadow-sm ${chat.role === 'user' ? 'bg-emerald-600 text-white rounded-2xl rounded-tr-none' : 'bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-tl-none'}`}>
                  <ReactMarkdown className="prose prose-sm prose-slate max-w-none">{chat.content}</ReactMarkdown>
                </div>
              </div>
            ))}
            {isTyping && <div className="animate-pulse flex gap-2 ml-2"><div className="w-2 h-2 bg-emerald-600 rounded-full" /><div className="w-2 h-2 bg-emerald-600 rounded-full" /><div className="w-2 h-2 bg-emerald-600 rounded-full" /></div>}
            <div ref={chatEndRef} />
          </div>

          <form onSubmit={askAI} className="p-4 bg-white border-t border-slate-100 flex gap-2">
            <input autoFocus type="text" value={userMsg} onChange={(e) => setUserMsg(e.target.value)} placeholder="Tanya sesuatu..." className="flex-1 bg-slate-100 border-none rounded-xl px-4 py-3 text-sm font-medium outline-none" />
            <button type="submit" disabled={isTyping} className="bg-slate-900 text-white p-3 rounded-xl hover:bg-slate-800 transition-all">
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default DetailPage;